import { useState, type FormEvent } from "react";
import { FlaskConical, Plus, ShieldAlert } from "lucide-react";

import {
  controlledPilotWorkspace,
  createVerticalPilot,
  transitionVerticalPilot,
} from "@/lib/api";
import { useData } from "@/lib/app-view";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Empty, ErrorState, Loading } from "@/components/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/page-header";
import { Textarea } from "@/components/ui/textarea";

export function ControlledPilots({
  organisationId,
  refresh,
  changed,
}: {
  organisationId: string;
  refresh: number;
  changed: () => void;
}) {
  const { data, error, loading } = useData(
    () => controlledPilotWorkspace(organisationId),
    [organisationId, refresh],
  );
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;

  async function advance(id: string, state: string) {
    const reason = window.prompt(`Reason for moving this pilot to ${state}`);
    if (!reason) return;
    setBusy(id);
    try {
      await transitionVerticalPilot(organisationId, id, state, reason);
      changed();
    } catch (reason) {
      window.alert((reason as Error).message);
    } finally {
      setBusy("");
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="CONTROLLED PRODUCT VALIDATION"
        title="Controlled pilots"
        description="Govern one authorised vertical pilot at a time without converting plans or synthetic tests into customer evidence."
      >
        <Button className="min-h-11" onClick={() => setOpen(true)}>
          <Plus className="size-4" />
          Propose pilot
        </Button>
      </PageHeader>
      <section className="rounded-xl border border-[var(--warning-border)] bg-[var(--warning-soft)] p-4 text-sm text-[var(--warning)]">
        <ShieldAlert className="mr-2 inline size-4" />A pilot can be completed
        only after a passed, non-synthetic Golden Journey validation is
        recorded. Public reference permission defaults to off.
      </section>
      <section className="grid gap-4 lg:grid-cols-2">
        {data.pilots.map((pilot) => {
          const validation = data.validations.find(
            (row) =>
              row.pilot_id === pilot.id &&
              row.result === "passed" &&
              !row.contains_synthetic_data,
          );
          const next =
            pilot.status === "proposed"
              ? "under_review"
              : pilot.status === "under_review"
                ? "approved"
                : pilot.status === "approved"
                  ? "active"
                  : pilot.status === "active"
                    ? "completed"
                    : "";
          return (
            <article className="surface p-5" key={pilot.id}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="eyebrow">CONTROLLED PILOT</p>
                  <h2 className="mt-1 text-xl font-semibold">{pilot.name}</h2>
                </div>
                <Badge value={pilot.status} />
              </div>
              <p className="mt-3 text-sm text-[var(--text-secondary)]">
                Design-partner reference is kept private. Public reference:{" "}
                {pilot.public_reference_permission
                  ? "explicitly permitted"
                  : "not permitted"}
                .
              </p>
              <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                <Datum
                  label="Golden Journey"
                  value={
                    validation
                      ? "verified with real workflow"
                      : "not yet verified"
                  }
                />
                <Datum label="Rollback" value={pilot.rollback_plan} />
                <Datum label="Privacy" value={pilot.privacy_requirements} />
                <Datum
                  label="Proposed"
                  value={new Date(pilot.proposed_at).toLocaleDateString()}
                />
              </dl>
              {next && (
                <Button
                  className="mt-5 min-h-11 w-full"
                  variant="outline"
                  disabled={
                    busy === pilot.id || (next === "completed" && !validation)
                  }
                  onClick={() => void advance(pilot.id, next)}
                >
                  {busy === pilot.id
                    ? "Recording…"
                    : `Move to ${next.replace("_", " ")}`}
                </Button>
              )}
            </article>
          );
        })}
        {!data.pilots.length && (
          <Empty
            title="No controlled pilot proposed"
            description="Start with one authorised organisation, bounded scope, baseline, success measures and rollback plan."
          />
        )}
      </section>
      <PilotDialog
        open={open}
        onOpenChange={setOpen}
        organisationId={organisationId}
        templates={data.templates}
        onDone={changed}
      />
    </>
  );
}

function Datum({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-[var(--surface-secondary)] p-3">
      <dt className="text-xs text-[var(--text-muted)]">{label}</dt>
      <dd className="mt-1 text-sm font-semibold">{value}</dd>
    </div>
  );
}

function PilotDialog({
  open,
  onOpenChange,
  organisationId,
  templates,
  onDone,
}: {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  organisationId: string;
  templates: any[];
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await createVerticalPilot(organisationId, {
        templateVersionId: String(form.get("template")),
        name: String(form.get("name")),
        designPartnerReference: String(form.get("partner")),
        scope: { summary: String(form.get("scope")) },
        migrationPlan: String(form.get("migration")),
        supportOwnerId: String(form.get("owner")),
        rollbackPlan: String(form.get("rollback")),
        privacyRequirements: String(form.get("privacy")),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="CONTROLLED VALIDATION"
          title="Propose a controlled pilot"
          description="Record the authorised scope, ownership, privacy boundary and recovery path before review."
        />
        {error && <ErrorState message={error} />}
        <form className="grid gap-4 p-6" onSubmit={submit}>
          <Label>
            Template
            <select
              name="template"
              required
              className="mt-1 min-h-11 w-full rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3"
            >
              {templates.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name} · v{row.version}
                </option>
              ))}
            </select>
          </Label>
          <Label>
            Name
            <Input name="name" required />
          </Label>
          <Label>
            Private design-partner reference
            <Input name="partner" required />
          </Label>
          <Label>
            Support owner user ID
            <Input name="owner" required />
          </Label>
          <Label>
            Scope
            <Textarea name="scope" required />
          </Label>
          <Label>
            Migration plan
            <Textarea name="migration" required />
          </Label>
          <Label>
            Rollback plan
            <Textarea name="rollback" required />
          </Label>
          <Label>
            Privacy requirements
            <Textarea name="privacy" required />
          </Label>
          <Button className="min-h-11" disabled={busy}>
            <FlaskConical className="size-4" />
            {busy ? "Recording…" : "Record proposal"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
